/**
 * Flux Boutique custom JavaScript (Prestige 11.4).
 *
 * Speed rework (25 Sep 2026): both features below used to run a check on every single
 * change to the page while it loaded (every app injecting its widgets triggered them).
 * They now share ONE watcher that batches changes and checks at most once per frame,
 * using cheap live lookups. Behaviour is unchanged.
 *
 * 1. Searchanise product grid fixes (added 17 Sep 2026, launch checklist task 1).
 *    Searchanise renders the collection and search-results grids. Its "Add to cart"
 *    button silently adds the FIRST variant of a multi-variant product, and the
 *    theme's cart count and drawer do not update after it adds anything.
 *    - Cards for products with more than one variant are relabelled "Choose options"
 *      and the button opens the product page, so a size or fragrance is chosen on purpose.
 *    - Single-variant adds are left to Searchanise, then the theme's cart count and
 *      drawer are refreshed and the drawer opens, the same as a native add.
 *
 * 2. Smile rewards panel matches the site (added 21 Sep 2026). See block further down.
 *
 * Revert: restore the previous custom.js from the live theme.
 */
(() => {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CARD_CLASS = 'snize-product';
  const BUTTON = '.snize-action-button';
  const LABEL = 'Choose options';
  const cache = new Map();
  let pendingSnizeAdd = 0;

  const productInfo = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const match = link && link.pathname.match(/\/products\/([^/?#]+)/);
    return match ? { handle: match[1], url: `${root}products/${match[1]}` } : null;
  };

  const getProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(handle, fetch(`${root}products/${handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null));
    }
    return cache.get(handle);
  };

  const setLabel = (button) => {
    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    let node, done = false;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) {
        node.nodeValue = done ? '' : LABEL;
        done = true;
      }
    }
    if (!done) button.textContent = LABEL;
    button.setAttribute('aria-label', LABEL);
  };

  const checkCard = async (card) => {
    if (card.dataset.fluxVariants) return card.dataset.fluxVariants;
    const info = productInfo(card);
    if (!info) return (card.dataset.fluxVariants = 'unknown');
    const product = await getProduct(info.handle);
    if (!product || !product.variants) return (card.dataset.fluxVariants = 'unknown');
    card.dataset.fluxVariants = product.variants.length > 1 ? 'multi' : 'single';
    if (card.dataset.fluxVariants === 'multi') {
      const button = card.querySelector(BUTTON);
      if (button) setLabel(button);
    }
    return card.dataset.fluxVariants;
  };

  const seen = new WeakSet();
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            checkCard(entry.target);
          }
        });
      }, { rootMargin: '400px 0px' })
    : null;

  // Live collection: updates itself, costs nothing to hold, cheap to read.
  const cards = document.getElementsByClassName(CARD_CLASS);
  const scanCards = () => {
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (seen.has(card)) continue;
      seen.add(card);
      observer && !document.hidden ? observer.observe(card) : checkCard(card);
    }
  };

  window.addEventListener('click', async (event) => {
    const button = event.target.closest && event.target.closest(BUTTON);
    const card = button && button.closest('.' + CARD_CLASS);
    if (!card) return;
    if (button.classList.contains('size-button-view-cart')) return;

    const state = card.dataset.fluxVariants;
    if (state === 'single' || state === 'unknown') {
      pendingSnizeAdd = Date.now();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    const info = productInfo(card);
    const result = await checkCard(card);
    if (result === 'multi' && info) {
      window.location.href = info.url;
    } else {
      pendingSnizeAdd = Date.now();
      button.click();
    }
  }, true);

  const afterAdd = () => {
    if (Date.now() - pendingSnizeAdd > 10000) return;
    pendingSnizeAdd = 0;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      setTimeout(() => drawer.show(), 400);
    }
  };

  const isAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isAdd(url)) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) afterAdd();
      });
    }
    return originalOpen.call(this, method, url, ...rest);
  };

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const request = originalFetch.apply(this, arguments);
    if (isAdd(url)) {
      request.then((response) => { if (response.ok) afterAdd(); }).catch(() => {});
    }
    return request;
  };

  /* ------------------------------------------------------------------------
     Smile rewards panel matches the site (added 21 Sep 2026)
     The Smile panel (opened by the Rewards link, the launcher and #smile-home)
     renders inside an about:blank iframe (iframe.smile-panel-frame), so the
     theme's CSS never reaches it and Smile's Appearance settings only cover
     colours. Because the frame is same-origin, a <style> is dropped into it:
     Jost from the theme's own font CDN, charcoal text, weight-400 headings,
     square cards with a hairline border instead of rounded shadowed ones, the
     theme's uppercase tracked button style on real buttons only (list rows keep
     sentence case), and a soft shadow behind the white text on the banner image.
     Smile's own colour settings (all Primary #535353) are untouched.
     Re-applies automatically if Smile rebuilds the frame.
     ------------------------------------------------------------------------ */
  const SMILE_CSS = [
    '@font-face { font-family: "Jost"; font-weight: 400; font-style: normal; font-display: swap; src: url("https://fluxboutique.co.nz/cdn/fonts/jost/jost_n4.d47a1b6347ce4a4c9f437608011273009d91f2b7.woff2") format("woff2"), url("https://fluxboutique.co.nz/cdn/fonts/jost/jost_n4.791c46290e672b3f85c3d1c651ef2efa3819eadd.woff") format("woff"); }',
    'html, body, .frame-root, .frame-root * { font-family: "Jost", sans-serif !important; }',
    '.frame-root { color: #535353 !important; }',
    '.frame-root .display, .frame-root .heading, .frame-root h1, .frame-root h2, .frame-root h3, .frame-root h4, .frame-root strong, .frame-root b { font-weight: 400 !important; }',
    '.frame-root .header-primary.display { font-size: 30px !important; letter-spacing: .06em !important; }',
    '.frame-root .header-secondary.heading { font-size: 12px !important; letter-spacing: .2em !important; text-transform: uppercase !important; font-weight: 400 !important; }',
    '.frame-root .header-primary, .frame-root .header-secondary { text-shadow: 0 1px 10px rgba(0,0,0,.45) !important; }',
    '.frame-root .panel-card-container, .frame-root .card, .frame-root [class*="card-container"] { border-radius: 0 !important; box-shadow: none !important; border: 1px solid #e5e5e5 !important; }',
    '.frame-root .panel-container { background: #ffffff !important; }',
    '.frame-root .list-item-title, .frame-root .card-list-item-title { font-weight: 400 !important; color: #535353 !important; }',
    '.frame-root .list-item-subtitle, .frame-root .text-muted { color: #8a8a8a !important; }',
    '.frame-root .btn { border-radius: 0 !important; text-transform: uppercase !important; letter-spacing: .2em !important; font-size: 12px !important; font-weight: 400 !important; box-shadow: none !important; }',
    '.frame-root .btn-primary, .frame-root .btn.btn-primary { background: #535353 !important; border-color: #535353 !important; color: #fff !important; }',
    '.frame-root input, .frame-root .form-control { border-radius: 0 !important; border: 1px solid #e5e5e5 !important; box-shadow: none !important; color: #535353 !important; }',
    '.frame-root input:focus, .frame-root .form-control:focus { border-color: #535353 !important; }',
    '.frame-root [class*="badge"], .frame-root [class*="pill"], .frame-root .rounded, .frame-root .rounded-pill { border-radius: 0 !important; }',
    '.frame-root .text-danger, .frame-root [class*="error"] { color: #535353 !important; }'
  ].join('\n');

  const smileFrames = document.getElementsByClassName('smile-panel-frame');
  const applySmile = (frame) => {
    let doc;
    try { doc = frame.contentDocument; } catch (e) { return; }
    if (!doc || !doc.head || doc.getElementById('flux-smile-panel-style')) return;
    const style = doc.createElement('style');
    style.id = 'flux-smile-panel-style';
    style.textContent = SMILE_CSS;
    doc.head.appendChild(style);
  };
  let smileTimer = null;
  const scanSmile = () => {
    if (!smileFrames.length) return;
    for (let i = 0; i < smileFrames.length; i++) {
      if (smileFrames[i].tagName === 'IFRAME') applySmile(smileFrames[i]);
    }
    // Smile can rewrite the frame document after it first appears: re-check briefly,
    // but only once a frame actually exists.
    if (!smileTimer) {
      let tries = 0;
      smileTimer = setInterval(() => {
        for (let i = 0; i < smileFrames.length; i++) {
          if (smileFrames[i].tagName === 'IFRAME') applySmile(smileFrames[i]);
        }
        if (++tries > 60) { clearInterval(smileTimer); smileTimer = 'done'; }
      }, 500);
    }
  };

  /* One shared, batched watcher for both features. */
  let queued = false;
  const runScans = () => {
    queued = false;
    scanCards();
    scanSmile();
  };
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(runScans);
  }).observe(document.documentElement, { childList: true, subtree: true });
  runScans();
})();